import { useCallback, useEffect, useState, useMemo, useRef, } from "react";
import LudoBetModal
  from "./components/LudoBetModal";

import LudoMatching
  from "./components/LudoMatching";

import axios from "axios";

import toast from "react-hot-toast";

import "./ludo.css";

import LudoBoard from "./components/LudoBoard";
import GameMode from "./components/GameMode";
import PlayerProfile from "./components/PlayerProfile";
import { calculateLudoWinning } from "./components/ludoPrize";

function LudoGame() {
  const resultSubmittedRef = useRef(false);
  const GAME_STORAGE_KEY = "ludo_game_state";
  const [restoringGame, setRestoringGame] = useState(true);
  const [wallet, setWallet] = useState(0);
  const [betAmount, setBetAmount] = useState(0);
  const [entryBet, setEntryBet] = useState(0);
  const [gameResetKey, setGameResetKey] = useState(0);

  const [boardState, setBoardState] =
    useState(null);

  const [savedBoardState, setSavedBoardState] =
    useState(null);

  const [dice, setDice] =
    useState(1);

  const [gameMode, setGameMode] =
    useState(null);

  const [activePlayers, setActivePlayers] =
    useState([]);

  const [currentPlayer, setCurrentPlayer] =
    useState("green");

  const [rolling, setRolling] =
    useState(false);

  const [rollId, setRollId] =
    useState(0);

  const [canRoll, setCanRoll] =
    useState(true);

  const [autoPlay, setAutoPlay] = useState(false);
  const [autoPlayTimeout, setAutoPlayTimeout] = useState(false);

  const [playerProfiles, setPlayerProfiles] =
    useState({});

  const [menuOpen, setMenuOpen] = useState(false);

  const [selectedMode, setSelectedMode] =
    useState(null);

  const [showBetModal, setShowBetModal] =
    useState(false);

  const [showMatching, setShowMatching] =
    useState(false);

  const [betLoading, setBetLoading] =
    useState(false);

  const [ludoGameCode, setLudoGameCode] =
    useState(null);

  // ========================================
  // HISTORY / RULES / REPLAY
  // ========================================

  const [showLudoHistory, setShowLudoHistory] = useState(false);
  const [ludoHistory, setLudoHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [showLudoRules, setShowLudoRules] = useState(false);
  const [ludoReplayGame, setLudoReplayGame] = useState(null);
  const [ludoReplayIndex, setLudoReplayIndex] = useState(0);
  const [ludoReplayPlaying, setLudoReplayPlaying] = useState(false);

  const baseUrl =
    process.env.REACT_APP_API_URL;

  const token =
    localStorage.getItem("token");

  /* 🔊 LUDO AUDIO */
  const audioFiles = useMemo(() => ({
    click: new Audio("/assets/ludo/audio/click.mp3"),
    matching: new Audio("/assets/ludo/audio/win.mp3"),

    run: new Audio("/assets/ludo/audio/click.mp3"),
    open: new Audio("/assets/ludo/audio/open.mp3"),
    kill: new Audio("/assets/ludo/audio/kill.mp3"),
    home: new Audio("/assets/ludo/audio/home.mp3"),
    win: new Audio("/assets/ludo/audio/win.mp3"),
    dice: new Audio("/assets/ludo/audio/dice.mp3"),
    safe: new Audio("/assets/ludo/audio/safe.mp3"),
  }), []);

  const playSound = useCallback((sound) => {
    const audio = audioFiles[sound];

    if (!audio) {
      return;
    }

    audio.currentTime = 0;

    audio.play().catch(() => { });
  }, [audioFiles]);


  useEffect(() => {

    const fetchUserBalance = async () => {

      try {

        if (!token) {
          return;
        }

        const response = await axios.get(
          `${baseUrl}/api/webapi/userInfo`,
          {
            headers: {
              Authorization: token,
            },
          }
        );

        if (
          response.data?.status &&
          response.data?.data
        ) {

          const money =
            Number(
              response.data.data.money || 0
            );

          setWallet(money);
        }

      } catch (error) {

        console.error(
          "FETCH USER BALANCE ERROR:",
          error
        );

      }

    };

    fetchUserBalance();

  }, [baseUrl]);


  // ========================================
  // SELECT GAME MODE
  // ========================================

  const handleGameModeSelect = (mode) => {

    // =====================================
    // OFFLINE 4 PLAYER
    // =====================================

    if (mode.offline) {

      const players =
        mode.activePlayers || [];

      const randomProfiles =
        generateRandomProfiles(
          players
        );

      playSound("click");

      setGameMode(mode);

      setActivePlayers(
        players
      );

      setPlayerProfiles(
        randomProfiles
      );

      setCurrentPlayer(
        players[0]
      );

      setDice(1);

      setRollId(0);

      setCanRoll(true);

      return;
    }


    // =====================================
    // ONLINE / BET MODE
    // =====================================

    setSelectedMode(mode);

    setShowBetModal(true);
  };

  const handlePlayAgain = () => {
    console.log("LUDO PLAY AGAIN → NEW BET");

    // Remove previous saved game
    localStorage.removeItem(GAME_STORAGE_KEY);

    // Clear current game
    setBoardState(null);
    setSavedBoardState(null);
    setLudoGameCode(null);

    // Reset bet
    setBetAmount(0);
    setEntryBet(0);

    // Stop matching
    setShowMatching(false);

    // Reset game values
    setDice(1);
    setRollId(0);
    setCanRoll(true);
    setCurrentPlayer("blue");

    // Clear profiles
    setPlayerProfiles({});

    // IMPORTANT:
    // Return to bet-selection screen
    // so LudoBetModal can actually render.
    setGameMode(null);

    // Keep selectedMode so the same mode
    // opens in the new bet modal.
    setShowBetModal(true);

    // Force fresh board next time
    setGameResetKey(prev => prev + 1);
  };


  // ========================================
  // RESTORE GAME AFTER REFRESH
  // ========================================

  useEffect(() => {

    try {

      const savedGame =
        localStorage.getItem(
          GAME_STORAGE_KEY
        );

      if (!savedGame) {
        setRestoringGame(false);
        return;
      }

      const game =
        JSON.parse(savedGame);

      if (
        !game ||
        !game.gameMode
      ) {
        setRestoringGame(false);
        return;
      }

      setGameMode(
        game.gameMode
      );

      setActivePlayers(
        game.activePlayers || []
      );

      if (game.playerProfiles) {

        setPlayerProfiles(
          game.playerProfiles
        );

      } else {

        const randomProfiles =
          generateRandomProfiles(
            game.activePlayers || []
          );

        setPlayerProfiles(
          randomProfiles
        );
      }

      setCurrentPlayer(
        game.currentPlayer ||
        game.activePlayers?.[0] ||
        "green"
      );

      setDice(
        game.dice ?? 1
      );

      setBetAmount(
        Number(game.betAmount ?? 0)
      );

      setEntryBet(
        Number(game.entryBet ?? 0)
      );

      setLudoGameCode(
        game.ludoGameCode ?? null
      );

      setSelectedMode(
        game.selectedMode ?? game.gameMode ?? null
      );

      // ==================================
      // RESET ROLL EVENT
      // ==================================
      // rollId is only an event trigger.
      // Never restore it after refresh.
      setRollId(0);

      if (game.boardState) {

        setSavedBoardState(
          game.boardState
        );

      } else {

        setSavedBoardState(null);

      }

      setAutoPlay(false);
      setAutoPlayTimeout(false);

    } catch (error) {

      console.error(
        "Failed to restore Ludo game:",
        error
      );

      localStorage.removeItem(
        GAME_STORAGE_KEY
      );

    } finally {

      setRestoringGame(false);

    }

  }, []);

  // ========================================
  // SAVE GAME
  // ========================================

  useEffect(() => {

    // DO NOT SAVE DURING INITIAL RESTORE
    if (restoringGame) {
      return;
    }

    if (!gameMode) {
      return;
    }


    const gameState = {

      gameMode,

      activePlayers,

      playerProfiles,

      currentPlayer,

      dice,

      boardState,

      // ADD THESE
      betAmount,
      entryBet,
      ludoGameCode,
      selectedMode,

    };


    localStorage.setItem(
      GAME_STORAGE_KEY,
      JSON.stringify(gameState)
    );

  }, [
    restoringGame,
    gameMode,
    activePlayers,
    playerProfiles,
    currentPlayer,
    dice,
    boardState,
    // ADD
    betAmount,
    entryBet,
    ludoGameCode,
    selectedMode,
  ]);

  // ========================================
  // ROLL DICE
  // ========================================

  const rollDice = useCallback(() => {

    if (rolling || !canRoll) {
      return;
    }

    playSound("dice");

    setRolling(true);
    setCanRoll(false);

    setTimeout(() => {

      const value =
        Math.floor(Math.random() * 6) + 1;

      setDice(value);

      setRollId(
        previous => previous + 1
      );

      setRolling(false);

    }, 800);

  }, [rolling, canRoll, playSound]);

  // ========================================
  // AUTO PLAY - PLAYER ONLY
  // ========================================

  useEffect(() => {

    // Auto Play OFF
    if (!autoPlay) {
      return;
    }

    if (!gameMode) {
      return;
    }

    // ========================================
    // COMPUTER DOES NOT USE THIS EFFECT
    // ========================================

    const isComputerPlayer =
      gameMode?.computerPlayers?.includes(
        currentPlayer
      );

    if (isComputerPlayer) {
      return;
    }

    // ========================================
    // PLAYER ONLY
    // ========================================

    if (rolling) {
      return;
    }

    if (!canRoll) {
      return;
    }

    if (!boardState) {
      return;
    }

    if (boardState.winner) {
      return;
    }

    const timer = setTimeout(() => {

      rollDice();

    }, 1000);

    return () => {
      clearTimeout(timer);
    };

  }, [
    autoPlay,
    gameMode,
    currentPlayer,
    rolling,
    canRoll,
    boardState,
    rollDice
  ]);

  // ========================================
  // AUTO ENABLE AFTER 10 SECONDS
  // ========================================

  useEffect(() => {

    // Already Auto Play
    if (autoPlay) {
      return;
    }

    // No game
    if (!gameMode) {
      return;
    }

    // Game finished
    if (boardState?.winner) {
      return;
    }

    // Computer handles itself
    if (
      gameMode.id === "computer" &&
      currentPlayer === "green"
    ) {
      return;
    }

    // Dice is currently rolling
    if (rolling) {
      return;
    }

    // Dice already rolled
    // Now player needs to select token,
    // so don't start another roll timer.
    if (!canRoll) {
      return;
    }

    const timer = setTimeout(() => {

      // Player did nothing for 3 seconds
      setAutoPlay(true);
      setAutoPlayTimeout(true);

    }, 10000);

    return () => {
      clearTimeout(timer);
    };

  }, [
    autoPlay,
    gameMode,
    currentPlayer,
    rolling,
    canRoll,
    boardState,
  ]);


  // ========================================
  // COMPUTER AUTO ROLL
  // ========================================
  //
  // COMPUTER ALWAYS PLAYS.
  // autoPlay DOES NOT MATTER.
  // ========================================

  useEffect(() => {

    if (!gameMode) {
      return;
    }

    // ========================================
    // CHECK IF CURRENT PLAYER IS COMPUTER
    // ========================================

    const isComputerPlayer =
      gameMode?.computerPlayers?.includes(
        currentPlayer
      );

    if (!isComputerPlayer) {
      return;
    }

    // ========================================
    // WAIT FOR BOARD
    // ========================================

    if (!boardState) {
      return;
    }

    // ========================================
    // ALREADY ROLLED
    // ========================================

    if (boardState.hasRolled) {
      return;
    }

    // ========================================
    // CURRENTLY ROLLING
    // ========================================

    if (rolling) {
      return;
    }

    // ========================================
    // DICE NOT AVAILABLE
    // ========================================

    if (!canRoll) {
      return;
    }

    // ========================================
    // COMPUTER ALWAYS ROLLS
    // ========================================

    const timer = setTimeout(() => {

      rollDice();

    }, 1000);

    return () => {
      clearTimeout(timer);
    };

  }, [
    gameMode,
    currentPlayer,
    boardState,
    rolling,
    canRoll,
    rollDice
  ]);


  const PLAYER_PROFILES = [
    // International
    {
      name: "Rodolph",
      image: "https://i.pravatar.cc/150?img=12",
    },
    {
      name: "Isadora",
      image: "https://i.pravatar.cc/150?img=32",
    },
    {
      name: "Lota",
      image: "https://i.pravatar.cc/150?img=47",
    },
    {
      name: "Goncalo",
      image: "https://i.pravatar.cc/150?img=53",
    },
    {
      name: "Oliver",
      image: "https://i.pravatar.cc/150?img=14",
    },
    {
      name: "Sophia",
      image: "https://i.pravatar.cc/150?img=44",
    },

    // Indian
    {
      name: "Aarav",
      image: "https://i.pravatar.cc/150?img=68",
    },
    {
      name: "Priya",
      image: "https://i.pravatar.cc/150?img=49",
    },
    {
      name: "Rahul",
      image: "https://i.pravatar.cc/150?img=69",
    },
    {
      name: "Ananya",
      image: "https://i.pravatar.cc/150?img=47",
    },
    {
      name: "Arjun",
      image: "https://i.pravatar.cc/150?img=11",
    },
    {
      name: "Sneha",
      image: "https://i.pravatar.cc/150?img=44",
    },
    {
      name: "Rohan",
      image: "https://i.pravatar.cc/150?img=13",
    },
    {
      name: "Kavya",
      image: "https://i.pravatar.cc/150?img=45",
    },
    {
      name: "Vikram",
      image: "https://i.pravatar.cc/150?img=52",
    },
    {
      name: "Neha",
      image: "https://i.pravatar.cc/150?img=25",
    },
    {
      name: "Aditya",
      image: "https://i.pravatar.cc/150?img=61",
    },
    {
      name: "Pooja",
      image: "https://i.pravatar.cc/150?img=43",
    },
    {
      name: "Karan",
      image: "https://i.pravatar.cc/150?img=59",
    },
    {
      name: "Meera",
      image: "https://i.pravatar.cc/150?img=26",
    },
    {
      name: "Ravi",
      image: "https://i.pravatar.cc/150?img=65",
    },
    {
      name: "Simran",
      image: "https://i.pravatar.cc/150?img=29",
    },
    {
      name: "Amit",
      image: "https://i.pravatar.cc/150?img=67",
    },
    {
      name: "Isha",
      image: "https://i.pravatar.cc/150?img=31",
    },
    {
      name: "Nikhil",
      image: "https://i.pravatar.cc/150?img=70",
    },
    {
      name: "Shreya",
      image: "https://i.pravatar.cc/150?img=48",
    },
    {
      name: "Manish",
      image: "https://i.pravatar.cc/150?img=60",
    },
    {
      name: "Riya",
      image: "https://i.pravatar.cc/150?img=24",
    },
    {
      name: "Sandeep",
      image: "https://i.pravatar.cc/150?img=71",
    },
    {
      name: "Divya",
      image: "https://i.pravatar.cc/150?img=30",
    },
    {
      name: "Akash",
      image: "https://i.pravatar.cc/150?img=33",
    },
    {
      name: "Aditi",
      image: "https://i.pravatar.cc/150?img=36",
    },
    {
      name: "Mohit",
      image: "https://i.pravatar.cc/150?img=56",
    },
    {
      name: "Nandini",
      image: "https://i.pravatar.cc/150?img=35",
    },
    {
      name: "Varun",
      image: "https://i.pravatar.cc/150?img=54",
    },
    {
      name: "Muskan",
      image: "https://i.pravatar.cc/150?img=42",
    },
    {
      name: "Rohit",
      image: "https://i.pravatar.cc/150?img=39",
    },
    {
      name: "Sakshi",
      image: "https://i.pravatar.cc/150?img=41",
    },
    {
      name: "Yash",
      image: "https://i.pravatar.cc/150?img=58",
    },
    {
      name: "Tanvi",
      image: "https://i.pravatar.cc/150?img=50",
    },
  ];

  const generateRandomProfiles = (players) => {
    const shuffled = [...PLAYER_PROFILES]
      .sort(() => Math.random() - 0.5);

    const profiles = {};

    players.forEach((color, index) => {
      profiles[color] =
        shuffled[index % shuffled.length];
    });

    return profiles;
  };

  // ========================================
  // LUDO HISTORY
  // ========================================

  const handleLudoHistory = async () => {
    try {
      setHistoryLoading(true);

      const response = await axios.get(
        `${baseUrl}/api/ludo/history`,
        {
          headers: {
            Authorization: token,
          },
        }
      );

      if (!response.data?.success) {
        toast.error(
          response.data?.message || "Unable to load Ludo history"
        );
        return;
      }

      setLudoHistory(response.data.data || []);
      setShowBetModal(false);
      setShowLudoRules(false);
      setShowLudoHistory(true);
    } catch (error) {
      console.error("LUDO HISTORY ERROR:", error);
      toast.error(
        error.response?.data?.message || "Unable to load Ludo history"
      );
    } finally {
      setHistoryLoading(false);
    }
  };

  const normalizeReplayStates = (game) => {
    let states = game?.game_state_history || [];

    if (typeof states === "string") {
      try {
        states = JSON.parse(states);
      } catch {
        states = [];
      }
    }

    return Array.isArray(states) ? states : [];
  };


  // ========================================
  // NORMALIZE LUDO REPLAY BOARD
  // ========================================
  const normalizeReplayBoardState = (snapshot) => {

    let boardState =
      snapshot?.boardState || snapshot || {};

    // boardState can be JSON string
    if (typeof boardState === "string") {
      try {
        boardState = JSON.parse(boardState);
      } catch {
        boardState = {};
      }
    }

    let tokens = boardState?.tokens || [];

    // tokens can also be JSON string
    if (typeof tokens === "string") {
      try {
        tokens = JSON.parse(tokens);
      } catch {
        tokens = [];
      }
    }

    if (!Array.isArray(tokens)) {
      tokens = [];
    }

    // Keep exactly the token structure used by LudoBoard
    tokens = tokens.map((token, index) => ({
      id:
        token.id ||
        `${token.color || "blue"}-${token.tokenNumber || index + 1}`,

      color:
        String(token.color || "blue").toLowerCase(),

      tokenNumber:
        Number(token.tokenNumber) ||
        ((index % 4) + 1),

      status:
        String(token.status || "home").toLowerCase(),

      step:
        token.step === undefined ||
          token.step === null
          ? -1
          : Number(token.step),
    }));

    return {
      ...boardState,

      tokens,

      currentPlayer:
        boardState.currentPlayer || "blue",

      hasRolled:
        Boolean(boardState.hasRolled),

      winner:
        boardState.winner || null,
    };
  };

  const openLudoReplay = (game) => {
    const states = normalizeReplayStates(game);

    if (!states.length) {
        toast.error("Replay data not available");
        return;
    }

    // ALWAYS start a new replay from the beginning
    setLudoReplayPlaying(false);
    setLudoReplayIndex(0);

    setLudoReplayGame({
        ...game,
        game_state_history: states,
    });

    setShowLudoHistory(false);

    // Small delay so the replay is rendered at Step 1
    setTimeout(() => {
        setLudoReplayPlaying(false);
    }, 50);
};

  const closeLudoReplay = () => {
    setLudoReplayPlaying(false);
    setLudoReplayGame(null);
    setLudoReplayIndex(0);
  };

  useEffect(() => {
    if (!ludoReplayPlaying || !ludoReplayGame) {
        return;
    }

    const states =
        ludoReplayGame.game_state_history || [];

    if (
        ludoReplayIndex >=
        states.length - 1
    ) {
        setLudoReplayPlaying(false);
        return;
    }

    const timer = setTimeout(() => {
        setLudoReplayIndex(
            prev => prev + 1
        );
    }, 900);

    return () => {
        clearTimeout(timer);
    };
}, [
    ludoReplayPlaying,
    ludoReplayGame,
    ludoReplayIndex,
]);

  // ========================================
  // SAVE BOARD SNAPSHOT
  // ========================================

  useEffect(() => {
    if (restoringGame || !ludoGameCode || !gameMode || gameMode.offline) {
      return;
    }

    if (!boardState) {
      return;
    }

    const saveSnapshot = async () => {
      try {
        await axios.post(
          `${baseUrl}/api/ludo/save-state`,
          {
            gameCode: ludoGameCode,
            boardState,
            dice,
          },
          {
            headers: {
              Authorization: token,
              "Content-Type": "application/json",
            },
          }
        );
      } catch (error) {
        console.error("LUDO SNAPSHOT ERROR:", error);
      }
    };

    saveSnapshot();
  }, [
    restoringGame,
    ludoGameCode,
    gameMode?.offline,
    boardState,
    dice,
    baseUrl,
    token,
  ]);

  const placeLudoBet = async (amount) => {

    const bet = Number(amount);

    const multiplier =
      selectedMode?.id === "2-player"
        ? 2
        : selectedMode?.id === "3-player"
          ? 3
          : 4;

    const winningAmount = bet * multiplier;

    setEntryBet(bet);
    setBetAmount(winningAmount);

    if (!bet || bet <= 0) {
      toast.error("Invalid bet amount");
      return;
    }

    if (!selectedMode) {
      toast.error("Please select game mode");
      return;
    }

    if (bet > Number(wallet)) {
      toast.error("Insufficient balance");
      return;
    }

    try {

      setBetLoading(true);

      const response = await axios.post(
        `${baseUrl}/api/ludo/start`,
        {
          mode: selectedMode.id,
          betAmount: bet,
        },
        {
          headers: {
            Authorization: token,
            "Content-Type": "application/json",
          },
        }
      );

      if (!response.data?.success) {

        toast.error(
          response.data?.message ||
          "Unable to place bet"
        );

        return;
      }

      const multiplier =
        selectedMode?.id === "2-player"
          ? 2
          : selectedMode?.id === "3-player"
            ? 3
            : selectedMode?.id === "4-player"
              ? 4
              : 1;

      const winningAmount = bet * multiplier;

      setBetAmount(winningAmount);

      // Update wallet
      setWallet(
        Number(response.data.balance)
      );

      // Save game code
      setLudoGameCode(
        response.data.gameCode
      );

      setShowBetModal(false);

      setGameMode(selectedMode);

      setActivePlayers(
        selectedMode.activePlayers
      );

      const profiles =
        generateRandomProfiles(
          selectedMode.activePlayers
        );

      setPlayerProfiles(profiles);

      playSound("matching");

      setShowMatching(true);

    } catch (error) {

      console.error(
        "LUDO BET ERROR:",
        error
      );

      toast.error(
        error.response?.data?.message ||
        "Unable to place bet"
      );

    } finally {

      setBetLoading(false);
    }
  };

  const startMatchedLudoGame = () => {

    setShowMatching(false);

    const players =
      selectedMode?.activePlayers || [];

    setGameMode(selectedMode);

    setActivePlayers(players);

    // USER ALWAYS PLAYS BLUE
    setCurrentPlayer("blue");

    setDice(1);

    setRollId(0);

    setCanRoll(true);

  };

  // ========================================
  // LUDO RESULT / WIN / LOSE
  // ========================================

  const [
    resultSubmitting,
    setResultSubmitting
  ] = useState(false);


  useEffect(() => {

    // No winner yet
    if (!boardState?.winner) {
      return;
    }

    // Offline game has no bet
    if (gameMode?.offline) {
      return;
    }

    // No backend game
    if (!ludoGameCode) {
      return;
    }

    // Already submitting
    if (resultSubmitting) {
      return;
    }


    const submitLudoResult = async () => {

      try {

        setResultSubmitting(true);

        const response =
          await axios.post(
            `${baseUrl}/api/ludo/finish`,
            {
              gameCode:
                ludoGameCode,

              winnerColor:
                boardState.winner,

              boardState,
              dice,
            },
            {
              headers: {
                Authorization:
                  token,
              },
            }
          );


        if (
          !response.data?.success
        ) {

          toast.error(
            response.data?.message ||
            "Unable to save game result"
          );

          setResultSubmitting(false);

          return;
        }


        // =================================
        // UPDATE WALLET
        // =================================

        setWallet(
          Number(
            response.data.balance
          )
        );


        // =================================
        // SHOW RESULT
        // =================================

        if (
          response.data.result ===
          "win"
        ) {

          toast.success(
            `You won ₹${response.data.winAmount}`,
            {
              duration: 5000,
            }
          );

        } else {

          toast.error(
            "You lost this game",
            {
              duration: 5000,
            }
          );

        }


        // =================================
        // CLEAR GAME CODE
        // =================================

        setLudoGameCode(null);

      } catch (error) {

        console.error(
          "LUDO RESULT ERROR:",
          error
        );

        toast.error(
          error.response?.data?.message ||
          "Unable to save game result"
        );

        // Allow retry if API failed
        setResultSubmitting(false);

      }

    };

    submitLudoResult();

  }, [
    boardState?.winner,
    ludoGameCode,
    gameMode?.offline,
    resultSubmitting,
    baseUrl,
    token,
  ]);

  const submitLudoLose = async () => {

    if (resultSubmittedRef.current) {
      return;
    }

    if (!ludoGameCode) {
      return;
    }

    if (gameMode?.offline) {
      return;
    }

    resultSubmittedRef.current = true;

    try {

      console.log(
        "================================"
      );

      console.log(
        "LUDO EXIT → LOSE"
      );

      console.log(
        "GAME CODE:",
        ludoGameCode
      );

      console.log(
        "ENTRY BET:",
        entryBet
      );

      const response = await axios.post(
        `${baseUrl}/api/ludo/finish`,
        {
          gameCode: ludoGameCode,

          // Exit means computer wins
          winnerColor: "computer",

          result: "lose",

          reason: "exit",

          betAmount: Number(entryBet),

        },
        {
          headers: {
            Authorization: token,
          },
        }
      );

      console.log(
        "LUDO EXIT RESPONSE:",
        response.data
      );


      if (!response.data?.success) {

        resultSubmittedRef.current = false;

        toast.error(
          response.data?.message ||
          "Unable to save loss"
        );

        return;
      }


      // Update wallet from backend
      if (
        response.data.balance !==
        undefined
      ) {

        setWallet(
          Number(
            response.data.balance
          )
        );

      }


      setLudoGameCode(null);


      toast.error(
        `You lost ₹${Number(
          entryBet
        ).toLocaleString()}`,
        {
          duration: 4000,
        }
      );


    } catch (error) {

      console.error(
        "LUDO EXIT LOSE ERROR:",
        error
      );

      resultSubmittedRef.current = false;

      toast.error(
        error.response?.data?.message ||
        "Unable to save game result"
      );

    }

  };

  useEffect(() => {

    const handleBeforeUnload = () => {

      if (
        ludoGameCode &&
        gameMode &&
        !gameMode.offline &&
        !boardState?.winner
      ) {

        // Best effort only
        submitLudoLose();

      }
    };

    window.addEventListener(
      "beforeunload",
      handleBeforeUnload
    );

    return () => {

      window.removeEventListener(
        "beforeunload",
        handleBeforeUnload
      );

    };

  }, [
    ludoGameCode,
    gameMode,
    boardState?.winner,
  ]);

  const handleExit = async () => {

    playSound("click");

    setMenuOpen(false);

    // =================================
    // ACTIVE ONLINE/BET GAME
    // EXIT = LOSE
    // =================================

    if (
      ludoGameCode &&
      gameMode &&
      !gameMode.offline &&
      !boardState?.winner
    ) {

      await submitLudoLose();

    }

    // =================================
    // CLEAR LOCAL GAME
    // =================================

    localStorage.removeItem(
      GAME_STORAGE_KEY
    );

    setGameMode(null);
    setSelectedMode(null);

    setActivePlayers([]);

    setPlayerProfiles({});

    setBoardState(null);

    setSavedBoardState(null);

    setCurrentPlayer("blue");

    setDice(1);

    setRollId(0);

    setCanRoll(true);

    setLudoGameCode(null);

    setShowMatching(false);

    setShowBetModal(false);

    setBetAmount(0);

    setEntryBet(0);

    setAutoPlay(false);

  };

  const winningPrize = betAmount;
  const { netWinning, } = calculateLudoWinning(winningPrize);

  // ========================================
  // WAIT FOR LOCAL STORAGE RESTORE
  // ========================================

  if (restoringGame) {

    return (
      <div className="app">

        <div className="ludo-loading">
          Loading game...
        </div>

      </div>
    );

  }


  // ========================================
  // GAME MODE SCREEN
  // ========================================

  if (!gameMode) {

    return (
      <div className="app">

        <GameMode
          onSelect={handleGameModeSelect}
        />

        {/* =================================
                BET MODAL
            ================================= */}

        {showBetModal && selectedMode && (

          <LudoBetModal
            mode={selectedMode.id}
            loading={betLoading}
            playSound={playSound}
            wallet={wallet}
            historyLoading={historyLoading}

            onRules={() => {
              setShowBetModal(false);
              setShowLudoRules(true);
            }}

            onHistory={handleLudoHistory}

            onClose={() => {
              if (!betLoading) {
                setShowBetModal(false);
              }
            }}

            onConfirm={placeLudoBet}
          />

        )}

        {/* =================================
            LUDO RULES
        ================================= */}
        {showLudoRules && (
          <div className="ludo-info-overlay">
            <div className="ludo-rules-modal">
              <button
                className="ludo-modal-close"
                onClick={() => setShowLudoRules(false)}
              >
                ×
              </button>

              <div className="ludo-rules-title">
                <div>🎲</div>
                <h2>LUDO RULES</h2>
                <p>How to play</p>
              </div>

              <div className="ludo-rules-list">
                <div>
                  <strong>🎲 Roll the Dice</strong>
                  <span>Roll the dice on your turn to move your tokens.</span>
                </div>
                <div>
                  <strong>🏠 Open Token</strong>
                  <span>Roll a 6 to bring a token out of your home.</span>
                </div>
                <div>
                  <strong>🚀 Move Token</strong>
                  <span>Move your token according to the dice number.</span>
                </div>
                <div>
                  <strong>⚔️ Capture</strong>
                  <span>Land on an opponent token to send it back home.</span>
                </div>
                <div>
                  <strong>🛡️ Safe Cells</strong>
                  <span>Tokens on safe cells cannot be captured.</span>
                </div>
                <div>
                  <strong>🏆 Finish</strong>
                  <span>Complete all four tokens first to win the game.</span>
                </div>
                <div>
                  <strong>💰 Betting</strong>
                  <span>Your selected entry amount is deducted when the game starts.</span>
                </div>
              </div>

              <button
                className="ludo-rules-ok"
                onClick={() => setShowLudoRules(false)}
              >
                GOT IT
              </button>
            </div>
          </div>
        )}

        {/* =================================
            LUDO HISTORY
        ================================= */}
        {showLudoHistory && (
          <div className="ludo-info-overlay">
            <div className="ludo-history-modal">
              <button
                className="ludo-modal-close"
                onClick={() => setShowLudoHistory(false)}
              >
                ×
              </button>

              <div className="ludo-rules-title">
                <div>📜</div>
                <h2>GAME HISTORY</h2>
                <p>Your previous Ludo games</p>
              </div>

              <div className="ludo-history-list">
                {ludoHistory.length === 0 ? (
                  <div className="ludo-empty-history">
                    <div>🎲</div>
                    <h3>No Games Yet</h3>
                    <p>Your completed Ludo games will appear here.</p>
                  </div>
                ) : (
                  ludoHistory.map((game) => {
                    const replayStates = normalizeReplayStates(game);

                    return (
                      <div
                        className="ludo-history-card"
                        key={game.id || game.game_code}
                      >
                        <div>
                          <strong>{game.game_code}</strong>
                          <small>
                            {game.mode} • Bet ₹
                            {Number(game.bet_amount || 0).toLocaleString()}
                          </small>
                          <small>
                            {game.finished_at
                              ? new Date(game.finished_at).toLocaleString()
                              : ""}
                          </small>
                        </div>

                        <span className={`ludo-history-result ${game.result}`}>
                          {game.result === "win"
                            ? "WON"
                            : game.result === "lose"
                              ? "LOST"
                              : "DRAW"}
                        </span>

                        <strong>
                          {game.result === "win"
                            ? `+₹${Number(game.win_amount || 0).toLocaleString()}`
                            : game.result === "draw"
                              ? `+₹${Number(game.win_amount || 0).toLocaleString()}`
                              : "-₹"+Number(game.bet_amount || 0).toLocaleString()}
                        </strong>

                        {/* <button
                          type="button"
                          disabled={!replayStates.length}
                          onClick={() => openLudoReplay(game)}
                        >
                          ▶ VIEW GAME
                        </button> */}
                      </div>
                    );
                  })
                )}
              </div>
            </div>
          </div>
        )}

        {/* =================================
            LUDO REPLAY
        ================================= */}
        {ludoReplayGame && (
          <div className="ludo-info-overlay">
            <div className="ludo-replay-modal">
              <button
                className="ludo-modal-close"
                onClick={closeLudoReplay}
              >
                ×
              </button>

              <div className="ludo-rules-title">
                <div>🎮</div>
                <h2>GAME REPLAY</h2>
                <p>
                  {ludoReplayGame.game_code} • {ludoReplayGame.mode}
                </p>
              </div>

              {(() => {
                const states = ludoReplayGame.game_state_history || [];
                const snapshot = states[ludoReplayIndex] || {};

                const replayMode = {
                  id: ludoReplayGame.mode,
                  offline: true,
                  activePlayers: ["blue", "green", "yellow", "red"],
                  computerPlayers: [],
                };

                const replayBoardState = normalizeReplayBoardState(snapshot);

                return (
                  <>
                    <div className="ludo-replay-board">
                      <LudoBoard
                        key={`replay-${ludoReplayIndex}-${JSON.stringify(
                          replayBoardState.tokens
                        )}`}
                        dice={Number(snapshot.dice || 1)}
                        rollId={0}
                        activePlayers={replayMode.activePlayers}
                        gameMode={replayMode}
                        autoPlay={false}
                        savedBoardState={{
                          ...replayBoardState,
                          tokens: replayBoardState.tokens.map((token, index) => ({
                            id:
                              token.id ||
                              `${token.color}-${token.tokenNumber || index + 1}`,

                            color: String(token.color).toLowerCase(),

                            tokenNumber:
                              Number(token.tokenNumber) ||
                              ((index % 4) + 1),

                            status: token.status || "HOME",

                            step:
                              token.step === undefined ||
                                token.step === null
                                ? -1
                                : Number(token.step),
                          })),
                        }}
                        playerProfiles={{}}
                        playSound={() => { }}
                        betAmount={Number(ludoReplayGame.bet_amount || 0)}
                        entryBet={Number(ludoReplayGame.bet_amount || 0)}
                        onRollAvailabilityChange={() => { }}
                        onCurrentPlayerChange={() => { }}
                      />
                    </div>

                    <div className="ludo-replay-info">
                      <strong>
                        {snapshot.boardState?.currentPlayer ||
                          snapshot.currentPlayer ||
                          "START"}
                      </strong>
                      <span>
                        Step {ludoReplayIndex + 1} / {states.length}
                      </span>
                    </div>

                    <div className="ludo-replay-controls">
                      <button
                        disabled={ludoReplayIndex === 0}
                        onClick={() => {
                          setLudoReplayPlaying(false);
                          setLudoReplayIndex(0);
                        }}
                      >
                        ⏮
                      </button>

                      <button
                        disabled={ludoReplayIndex === 0}
                        onClick={() => {
                          setLudoReplayPlaying(false);
                          setLudoReplayIndex((p) => Math.max(0, p - 1));
                        }}
                      >
                        ◀
                      </button>

                      <button
                        className="ludo-replay-play"
                        onClick={() => {
                          if (ludoReplayIndex >= states.length - 1) {
                            setLudoReplayIndex(0);
                            setLudoReplayPlaying(true);
                          } else {
                            setLudoReplayPlaying((p) => !p);
                          }
                        }}
                      >
                        {ludoReplayPlaying ? "❚❚" : "▶"}
                      </button>

                      <button
                        disabled={ludoReplayIndex >= states.length - 1}
                        onClick={() => {
                          setLudoReplayPlaying(false);
                          setLudoReplayIndex((p) =>
                            Math.min(states.length - 1, p + 1)
                          );
                        }}
                      >
                        ▶
                      </button>

                      <button
                        disabled={ludoReplayIndex >= states.length - 1}
                        onClick={() => {
                          setLudoReplayPlaying(false);
                          setLudoReplayIndex(states.length - 1);
                        }}
                      >
                        ⏭
                      </button>
                    </div>
                  </>
                );
              })()}
            </div>
          </div>
        )}

      </div>
    );

  }

  // ========================================
  // GAME
  // ========================================

  return (
    <div className="ludo-page">

      {
        showMatching && selectedMode && (

          <LudoMatching
            mode={selectedMode}
            playerProfiles={
              playerProfiles
            }
            onComplete={
              startMatchedLudoGame
            }
          />

        )
      }

      <div className="app">

        <div className="game-area">

          {/* ========================================
              GAME HEADER
          ======================================== */}
          <div className="game-header">

            {/* MENU */}
            <div className="menu-wrapper">

              <button
                className="header-menu"
                onClick={() => setMenuOpen(prev => !prev)}
              >
                <span></span>
                <span></span>
                <span></span>
              </button>

              {menuOpen && (
                <div className="game-menu">

                  <button
                    className="menu-item exit-item"
                    onClick={handleExit}
                  >
                    🚪 Exit Game
                  </button>

                </div>
              )}

            </div>

            {/* CENTER COINS */}
            <div className="header-coins">

              <div className="coin-stack">
                💎
              </div>

              <div className="coin-amount">
                {netWinning}
              </div>

            </div>

            {/* GEMS / BALANCE */}
            <div className="header-gems">

              <div className="gem-icon">
                💎
              </div>

              <span className="gem-amount">
                {wallet.toFixed(2)}
              </span>

              {/* <button className="gem-plus">
              +
            </button> */}

            </div>

          </div>

          <button
            type="button"
            className={`auto-play-btn ${autoPlay ? "auto-play-active" : ""
              }`}
            onClick={() => setAutoPlay(prev => !prev)}
          >
            {autoPlay ? "Auto Play ON" : "Auto Play OFF"}
          </button>


          {/* ========================================
              PLAYER PROFILES
          ======================================== */}

          {activePlayers.map((color, index) => {

            const profile = playerProfiles[color];

            if (!profile) {
              return null;
            }

            const isComputerMode =
              gameMode?.computerPlayers?.includes(color);

            // BLUE = YOU
            const isYou =
              gameMode?.id !== "offline-4-player" &&
              color === "blue";

            const playerName =
              isYou
                ? "You"
                : profile.name;

            const isComputer =
              isComputerMode && !isYou;

            return (
              <PlayerProfile
                key={color}
                color={color}

                name={playerName}

                // Random image for everyone,
                // including You
                image={profile.image}

                isActive={
                  currentPlayer === color
                }

                isComputer={isComputer}

                dice={dice}
                rolling={rolling}
                onRoll={rollDice}

                canRoll={
                  canRoll &&
                  currentPlayer === color
                }

                hasRolled={
                  boardState?.hasRolled ?? false
                }
              />
            );
          })}


          {/* ========================================
              LUDO BOARD
          ======================================== */}

          <div className="board-wrapper">

            <LudoBoard
              key={gameResetKey}
              dice={dice}
              rollId={rollId}
              activePlayers={activePlayers}
              gameMode={gameMode}
              autoPlay={autoPlay}
              savedBoardState={savedBoardState}
              playerProfiles={playerProfiles}
              playSound={playSound}
              betAmount={betAmount}
              entryBet={entryBet}
              onPlayAgain={handlePlayAgain}

              onRollAvailabilityChange={
                setCanRoll
              }

              onCurrentPlayerChange={
                setCurrentPlayer
              }

              onGameStateChange={
                setBoardState
              }
            />

          </div>

        </div>

      </div>
    </div>
  );
}

export default LudoGame;